import { TOAST_TYPE } from '@base'
import { updateInvoiceV2 } from '@data'
import { EnquiryItem } from '@data/model'
import { showToast } from '@data/state/action'
import { updateDrawerMeta } from '@data/state/action/root'
import { strings } from '@constants'

/**
 * Returns resolved order_items for the invoice payload.
 * If invoiceData already has order_items, returns them unchanged.
 * Otherwise derives them from order using the same supplier/buyer branching
 * as index.jsx so the backend always receives a populated list.
 */
export const resolveOrderItems = (invoiceData, order, supplierPoNumber) => {
	if (invoiceData?.order_items?.length) return invoiceData.order_items
	let rawItems
	if (supplierPoNumber) {
		const supplier = order?.supplier_list?.find((_s) => _s?.supplier_po_number === supplierPoNumber)
		rawItems = supplier?.order_items?.map((item) => ({ ...item, ordered_weight: item?.allotted_weight })) ?? []
	} else {
		rawItems = order?.buyer_list?.[0]?.order_items ?? []
	}
	return rawItems.map((_o) => new EnquiryItem(_o).getOrderItemDetails())
}

/**
 * `vendor_bill_no` always goes to the API as an array of strings, whatever shape it arrives in
 * (array, single string, or missing).
 */
export const toVendorBillList = (value) => {
	if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean)
	return String(value ?? '')
		.split(',')
		.map((item) => item.trim())
		.filter(Boolean)
}

/**
 * Base set of invoice fields sent on every save, shared by the Basic / Vehicle / POD /
 * Serviced Quantity / Other Charges cards. Each card spreads this and overrides only
 * the fields it actually edits.
 */
export const buildBaseInvoicePayload = (invoiceData, order, supplierPoNumber) => ({
	invoice_number: invoiceData?.invoice_number,
	original_invoice_number: invoiceData?.original_invoice_number || invoiceData?.invoice_number,
	invoice_amount: invoiceData?.invoice_amount,
	invoice_date: invoiceData?.invoice_date,
	driver_number: invoiceData?.driver_number,
	vehicle_number: invoiceData?.vehicle_number,
	delivery_date: invoiceData?.delivery_date,
	rejected_weight: invoiceData?.rejected_weight,
	dispatch_location: invoiceData?.dispatch_location,
	delivery_status: invoiceData?.delivery_status ?? null,
	vendor_bill_no: toVendorBillList(invoiceData?.vendor_bill_no),
	oracle_invoice_internal_id: invoiceData?.oracle_invoice_internal_id,
	order_items: resolveOrderItems(invoiceData, order, supplierPoNumber),
})

/**
 * Shared invoice-save flow used by the Basic / Vehicle / POD cards.
 * Calls updateInvoiceV2, syncs the drawer state with the freshest invoice, and
 * fires the standard success/error toast. Kept in one place so the three cards
 * do not duplicate it.
 *
 * @returns {Promise<boolean>} true on success, false on failure.
 */
export const saveInvoice = async ({ payload, fallbackInvoice, order, invoiceData, dispatch, supplierPoNumber, update }) => {
	try {
		const response = await updateInvoiceV2(payload, order?.order_id)
		let updatedInvoice
		if (supplierPoNumber) {
			const supplier = response?.data?.supplier_list?.find((s) => s?.supplier_po_number === supplierPoNumber)
			updatedInvoice = supplier?.supplier_invoices?.find((i) => i.invoice_number === invoiceData?.invoice_number)
		} else {
			updatedInvoice = response?.data?.buyer_list?.[0]?.buyer_invoices?.find((i) => i.invoice_number === invoiceData?.invoice_number)
		}
		dispatch(updateDrawerMeta({ order: response?.data, invoiceData: updatedInvoice ?? fallbackInvoice }))
		dispatch(showToast(true, strings('msg_invoice_details_updated'), { type: TOAST_TYPE.SUCCESS }))
		update?.()
		return true
	} catch (e) {
		const serverMsg = e?.response?.data?.message || e?.response?.data?.error
		dispatch(showToast(true, serverMsg || strings('msg_failed_to_update_invoice_details'), { type: TOAST_TYPE.ERROR }))
		return false
	}
}
